import { useRef, useState, type ReactNode } from "react";
import type {
	DraftSummary,
	ExpandedKey,
	ProfileSummary,
	ProjectSummary,
	SessionSummary,
	SidebarCategory,
	SidebarView,
	User,
} from "../types";
import {
	ArchiveIcon,
	ChevronDownIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	EyeOffIcon,
	FolderIcon,
	FolderPlusIcon,
	GearIcon,
	LogOutIcon,
	MoonIcon,
	PencilIcon,
	PlusIcon,
	StarIcon,
	SunIcon,
	TagIcon,
} from "../icons";
import { Avatar } from "../avatar";
import { useIsNarrow } from "../viewport";
import { canFile } from "./filing";
import { Badge, NAV_PAGES, type NavBadges, type NavPage, type Page } from "./Rail";
import {
	conversationCount,
	groupSessions,
	sessionTitle,
	sharingLabel,
	type ProjectGroup,
	type SessionGroup,
} from "./sessionGroups";
import { arrangeSidebar, OTHER_NAME, type ProfileCategory } from "./sidebarArrangement";
import { CATEGORY_NAME_MAX, projectKey } from "../../../shared/wire/sidebar.ts";

function when(ts: number): string {
	const d = new Date(ts);
	const today = new Date();
	const sameDay = d.toDateString() === today.toDateString();
	return sameDay
		? d.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" })
		: d.toLocaleDateString("zh-CN", { month: "numeric", day: "numeric" });
}

/** What is wrong with a profile that takes no new conversation, or null. */
function problemOf(group: SessionGroup): string | null {
	if (group.status === "broken") return "加载失败";
	return group.status === "ok" && group.summary ? null : "profile 已不存在";
}

/** The profile's most recently updated conversation, wherever it is filed. */
function latestOf(group: SessionGroup): SessionSummary | null {
	const all = [...group.sessions, ...group.projects.flatMap((p) => p.sessions), ...[...group.branches.values()].flat()];
	return all.reduce<SessionSummary | null>((best, s) => (!best || s.updatedAt > best.updatedAt ? s : best), null);
}

/**
 * A session row. Only the owner archives, so a session shared with me has no archive button.
 * Its branches sit indented under it, shut until asked for -- except that the row holding the
 * open conversation never hides it.
 */
function SessionRow(props: {
	session: SessionSummary;
	currentSessionId: string | null;
	/** Every branch in the group by its parent, so a branch's own branches nest too. */
	branches: Map<string, SessionSummary[]>;
	me: string;
	nameOf: (userId: string) => string;
	onOpen: (id: string) => void;
	onArchive: (id: string) => void;
	/** A branch row, drawn without an avatar under its parent. */
	nested?: boolean;
	/** Dragging a row onto a project files it there; absent where nothing can be filed. */
	onDrag?: (session: SessionSummary | null) => void;
}) {
	const { session, branches, currentSessionId } = props;
	const drag = props.onDrag && canFile(session, props.me) ? props.onDrag : undefined;
	const sharing = sharingLabel(session, props.me, props.nameOf);
	const kids = branches.get(session.id) ?? [];
	const [toggled, setToggled] = useState<boolean | null>(null);
	const open = toggled ?? kids.some((k) => holdsCurrent(k.id, branches, currentSessionId));
	const canArchive = session.owner === props.me;
	const swipe = useSwipe(canArchive);
	return (
		<>
			<div
				className={`session${session.id === currentSessionId ? " active" : ""}${swipe.revealed ? " revealed" : ""}`}
				{...swipe.handlers}
				draggable={drag ? true : undefined}
				onDragStart={
					drag &&
					((e) => {
						e.stopPropagation();
						e.dataTransfer.setData("text/plain", session.id);
						e.dataTransfer.effectAllowed = "move";
						drag(session);
					})
				}
				onDragEnd={drag && (() => drag(null))}
			>
				<button className="open" onClick={() => (swipe.revealed ? swipe.close() : props.onOpen(session.id))} style={swipe.style}>
					{!props.nested && <Avatar name={session.profile} size={40} />}
					<span className="session-text">
						<span className="title">{sessionTitle(session)}</span>
						{(sharing || kids.length > 0) && (
							<span className="meta">{[sharing, kids.length > 0 && `${kids.length} 个分支`].filter(Boolean).join(" · ")}</span>
						)}
					</span>
					<span className="when">{when(session.updatedAt)}</span>
				</button>
				{kids.length > 0 && (
					<button className="ghost branches" aria-expanded={open} title={open ? "收起分支" : "展开分支"} onClick={() => setToggled(!open)}>
						{open ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
					</button>
				)}
				{canArchive && (
					<button className="ghost archive" title="归档" onClick={() => props.onArchive(session.id)}>
						<ArchiveIcon size={17} />
						<span className="archive-label">归档</span>
					</button>
				)}
			</div>
			{open && kids.length > 0 && (
				<div className="session-branches">
					{kids.map((k) => (
						<SessionRow key={k.id} {...props} session={k} nested />
					))}
				</div>
			)}
		</>
	);
}

const SWIPE_OPEN = 88;

/** Swiping a row left on a touch screen reveals its archive action; a tap or a swipe back hides it. */
function useSwipe(enabled: boolean) {
	const [offset, setOffset] = useState(0);
	const [dragging, setDragging] = useState(false);
	const start = useRef<{ x: number; y: number; base: number; locked: boolean | null } | null>(null);
	const revealed = offset <= -SWIPE_OPEN / 2;
	const close = () => setOffset(0);
	if (!enabled) return { revealed: false, close, handlers: {}, style: undefined };
	return {
		revealed,
		close,
		style: offset !== 0 || dragging ? { transform: `translateX(${offset}px)`, transition: dragging ? "none" : undefined } : undefined,
		handlers: {
			onTouchStart: (e: React.TouchEvent) => {
				const t = e.touches[0]!;
				start.current = { x: t.clientX, y: t.clientY, base: offset, locked: null };
			},
			onTouchMove: (e: React.TouchEvent) => {
				const s = start.current;
				const t = e.touches[0];
				if (!s || !t) return;
				const dx = t.clientX - s.x;
				const dy = t.clientY - s.y;
				// Decide once whether this gesture is a swipe or a scroll.
				if (s.locked === null && Math.abs(dx) + Math.abs(dy) > 8) s.locked = Math.abs(dx) > Math.abs(dy);
				if (!s.locked) return;
				setDragging(true);
				setOffset(Math.max(-SWIPE_OPEN, Math.min(0, s.base + dx)));
			},
			onTouchEnd: () => {
				start.current = null;
				setDragging(false);
				setOffset((v) => (v <= -SWIPE_OPEN / 2 ? -SWIPE_OPEN : 0));
			},
		},
	};
}

/** Whether `id` is the open conversation or has it somewhere among its branches. */
function holdsCurrent(id: string, branches: Map<string, SessionSummary[]>, currentSessionId: string | null): boolean {
	if (id === currentSessionId) return true;
	return (branches.get(id) ?? []).some((k) => holdsCurrent(k.id, branches, currentSessionId));
}

/** Everything the sidebar can do to sessions and projects, shared by both layouts. */
interface ListActions {
	/** The person looking, and how to name anyone else, for the sharing label. */
	me: string;
	nameOf: (userId: string) => string;
	currentSessionId: string | null;
	currentProjectId: string | null;
	onOpenSession: (id: string) => void;
	onArchiveSession: (id: string) => void;
	onOpenProject: (id: string) => void;
	onNewSession: (profile: string, projectId?: string) => void;
	/** An anonymous conversation is never filed under a project, so it takes only a profile. */
	onNewAnonymousSession: (profile: string) => void;
	/** The folds the person left open; null without their view, when a project folds on this screen alone. */
	expanded: ReadonlySet<ExpandedKey> | null;
	onToggleFold: (key: ExpandedKey, open: boolean) => void;
	/** The conversation being dragged, which a project of its profile takes on drop. */
	dragging: SessionSummary | null;
	onDragSession?: (session: SessionSummary | null) => void;
	onFileSession?: (sessionId: string, projectId: string) => void;
}

/**
 * One project inside a profile's card: its title opens the project page, the toggle beside it
 * shows or hides its conversations, the plus starts a conversation filed under it, and its
 * conversations sit indented below. They are folded unless the person opened the project or it
 * holds the open conversation. The open project is marked the way the open conversation is, by
 * fill alone.
 */
function ProjectBlock({
	group,
	branches,
	actions,
}: {
	group: ProjectGroup;
	branches: Map<string, SessionSummary[]>;
	actions: ListActions;
}) {
	const { project } = group;
	const key = projectKey(project.id);
	const [local, setLocal] = useState(false);
	/* The open conversation the person folded this project over. It stays out of sight until another
	   conversation opens; coming back to it later opens the project again. */
	const [foldedOver, setFoldedOver] = useState<string | null>(null);
	if (foldedOver !== null && foldedOver !== actions.currentSessionId) setFoldedOver(null);
	const [over, setOver] = useState(false);
	const dragged = actions.dragging?.profile === project.profile ? actions.dragging : null;
	if (over && !dragged) setOver(false);
	const holds = group.sessions.some((s) => holdsCurrent(s.id, branches, actions.currentSessionId));
	const open = (actions.expanded ? actions.expanded.has(key) : local) || (holds && foldedOver !== actions.currentSessionId);
	const toggle = () => {
		setLocal(!open);
		setFoldedOver(open && holds ? actions.currentSessionId : null);
		actions.onToggleFold(key, !open);
	};
	return (
		<div
			className={`project-block${actions.currentProjectId === project.id ? " active" : ""}${over ? " drop-target" : ""}`}
			role="group"
			aria-label={project.title}
			onDragOver={
				dragged
					? (e) => {
							e.preventDefault();
							e.dataTransfer.dropEffect = "move";
							setOver(true);
						}
					: undefined
			}
			onDragLeave={(e) => {
				if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
			}}
			onDrop={
				dragged
					? (e) => {
							e.preventDefault();
							setOver(false);
							actions.onDragSession?.(null);
							actions.onFileSession?.(dragged.id, project.id);
						}
					: undefined
			}
		>
			<div className="project-row">
				<button
					className="open"
					onClick={() => actions.onOpenProject(project.id)}
					title={Object.values(project.direction).find((v) => v.trim()) ?? project.title}
				>
					<span className="glyph-circle">
						<FolderIcon size={19} className="project-glyph" />
					</span>
					<span className="session-text">
						<span className="title">{project.title}</span>
						<span className="meta">{group.sessions.length > 0 ? `${group.sessions.length} 个对话` : "暂无对话"}</span>
					</span>
				</button>
				{group.sessions.length > 0 && (
					<button
						className="fold"
						aria-expanded={open}
						aria-label={`${project.title} 中的对话`}
						title={open ? "收起对话" : "展开对话"}
						onClick={toggle}
					>
						{open ? <ChevronDownIcon size={16} /> : <ChevronRightIcon size={16} />}
					</button>
				)}
				<button
					className="icon"
					title={`在 ${project.title} 中新建对话`}
					aria-label={`在 ${project.title} 中新建对话`}
					onClick={() =>
						actions.onNewSession(project.profile, project.id)
					}
				>
					<PlusIcon size={17} />
				</button>
			</div>
			{open && (
				<div className="project-sessions">
					{group.sessions.map((s) => (
						<SessionRow
							key={s.id}
							nested
							session={s}
							currentSessionId={actions.currentSessionId}
							branches={branches}
							me={actions.me}
							nameOf={actions.nameOf}
							onOpen={actions.onOpenSession}
							onArchive={actions.onArchiveSession}
						/>
					))}
				</div>
			)}
		</div>
	);
}

/** A profile's projects, then its loose conversations. */
function GroupBody({
	group,
	actions,
}: {
	group: SessionGroup;
	actions: ListActions;
}) {
	const empty = group.projects.length === 0 && group.sessions.length === 0;
	return (
		<div className="sessions">
			{empty && <p className="hint empty">暂无对话</p>}
			{group.projects.length > 0 && (
				<>
					<h3 className="list-label">项目</h3>
					<div className="list-card">
						{group.projects.map((pg) => (
							<ProjectBlock
								key={pg.project.id}
								group={pg}
								branches={group.branches}
								actions={actions}
							/>
						))}
					</div>
				</>
			)}
			{group.sessions.length > 0 && (
				<>
					<h3 className="list-label">对话</h3>
					<div className="list-card">
						{group.sessions.map((s) => (
							<SessionRow
								key={s.id}
								session={s}
								currentSessionId={actions.currentSessionId}
								branches={group.branches}
								me={actions.me}
								nameOf={actions.nameOf}
								onOpen={actions.onOpenSession}
								onArchive={actions.onArchiveSession}
								onDrag={actions.onDragSession}
							/>
						))}
					</div>
				</>
			)}
		</div>
	);
}

/**
 * Pins a profile to the top of the person's sidebar, or takes the pin out. Every profile has one,
 * a broken or missing one too, so a pin set before it broke can still be taken out.
 */
function StarToggle(props: { profile: string; starred: boolean; onToggle: (profile: string, starred: boolean) => void }) {
	return (
		<button
			className="icon star-toggle"
			aria-pressed={props.starred}
			aria-label={`星标 ${props.profile}`}
			title={props.starred ? "取消星标" : "星标"}
			onClick={() => props.onToggle(props.profile, !props.starred)}
		>
			<StarIcon />
		</button>
	);
}

/**
 * First screen: one row per profile, with its newest conversation as the preview. The row opens the
 * profile's screen, where conversations start. A broken or deleted profile is a row like any other,
 * since its screen is the way to its old conversations. The star sits beside the row.
 */
function ProfileRow(props: {
	group: SessionGroup;
	starred: boolean;
	onToggleStar: (profile: string, starred: boolean) => void;
	onOpen: (profile: string) => void;
}) {
	const { group } = props;
	const problem = problemOf(group);
	const latest = latestOf(group);
	return (
		<div className={`profile-head${group.status === "ok" ? "" : " unavailable"}`}>
			<button className="profile-open" onClick={() => props.onOpen(group.profile)} title={`打开 ${group.profile}`}>
				<Avatar name={group.profile} size={48} />
				<span className="profile-text">
					<span className="name">{group.profile}</span>
					<span className={problem ? "preview problem" : "preview"}>{problem ?? (latest ? sessionTitle(latest) : "暂无对话")}</span>
				</span>
				{latest && <span className="when">{when(latest.updatedAt)}</span>}
			</button>
			<StarToggle profile={group.profile} starred={props.starred} onToggle={props.onToggleStar} />
		</div>
	);
}

/**
 * One of the person's categories, or Other, on the first screen. It is folded unless the person
 * opened it, and its profile rows exist only while it is open. The person's view says which folds
 * are open; without one, a fold opens and closes on this screen alone. A category of the person's
 * own is renamed or removed in place; Other is neither.
 */
function CategoryFold(props: {
	category: ProfileCategory;
	remembered: boolean;
	onToggle: (key: ExpandedKey, open: boolean) => void;
	onRename: (id: string, name: string) => Promise<boolean>;
	/** Its profiles go back to Other. */
	onRemove: (id: string) => Promise<boolean>;
	children: ReactNode;
}) {
	const { category } = props;
	const id = category.id;
	const [local, setLocal] = useState(false);
	const [editing, setEditing] = useState(false);
	const open = props.remembered ? category.expanded : local;
	return (
		<div className="profile-category" role="group" aria-label={category.name}>
			{editing && id ? (
				<CategoryNameForm
					label={`${category.name} 的新名字`}
					initial={category.name}
					submit="保存"
					onSubmit={async (name) => {
						if (await props.onRename(id, name)) setEditing(false);
					}}
					onCancel={() => setEditing(false)}
				>
					<button
						type="button"
						className="ghost"
						aria-label={`删除分类 ${category.name}`}
						title="删除分类"
						onClick={() => void props.onRemove(id)}
					>
						删除
					</button>
				</CategoryNameForm>
			) : (
				<div className="category-head">
					<button
						className="category-toggle"
						aria-expanded={open}
						onClick={() => {
							setLocal(!open);
							props.onToggle(category.key, !open);
						}}
					>
						<span className="name">{category.name}</span>
						{!open && (
							<span className="mini-avatars">
								{category.groups.slice(0, 3).map((g) => (
									<Avatar key={g.profile} name={g.profile} size={22} />
								))}
							</span>
						)}
						<span className="count">{category.groups.length}</span>
						{open ? <ChevronDownIcon size={18} /> : <ChevronRightIcon size={18} />}
					</button>
					{id && (
						<button
							className="icon category-edit"
							aria-label={`编辑分类 ${category.name}`}
							title="重命名或删除"
							onClick={() => setEditing(true)}
						>
							<PencilIcon size={14} />
						</button>
					)}
				</div>
			)}
			{open && <div className="category-profiles">{props.children}</div>}
		</div>
	);
}

/** A category's name typed in place. Whoever shows it closes it once the server has taken the name. */
function CategoryNameForm(props: {
	label: string;
	initial: string;
	submit: string;
	onSubmit: (name: string) => Promise<void>;
	onCancel: () => void;
	/** More that can be done to the category, beside saving its name. */
	children?: ReactNode;
}) {
	const [name, setName] = useState(props.initial);
	const [busy, setBusy] = useState(false);
	return (
		<form
			className="category-form"
			onSubmit={async (e) => {
				e.preventDefault();
				setBusy(true);
				await props.onSubmit(name.trim());
				setBusy(false);
			}}
		>
			<input
				aria-label={props.label}
				placeholder="分类名"
				value={name}
				maxLength={CATEGORY_NAME_MAX}
				autoFocus
				onChange={(e) => setName(e.target.value)}
			/>
			<button type="submit" disabled={busy || !name.trim()}>
				{props.submit}
			</button>
			{props.children}
			<button type="button" className="ghost" onClick={props.onCancel}>
				取消
			</button>
		</form>
	);
}

/** Starts a category of the person's own, under the others. */
function NewCategory({ onCreate }: { onCreate: (name: string) => Promise<boolean> }) {
	const [naming, setNaming] = useState(false);
	if (!naming) {
		return (
			<button className="new-category" onClick={() => setNaming(true)}>
				<PlusIcon size={18} /> 新建分类
			</button>
		);
	}
	return (
		<CategoryNameForm
			label="新分类名"
			initial=""
			submit="添加"
			onSubmit={async (name) => {
				if (await onCreate(name)) setNaming(false);
			}}
			onCancel={() => setNaming(false)}
		/>
	);
}

/**
 * Files a profile under one of the person's categories, or back under Other. It shows where the
 * profile is filed; opened, it lists every category with that one pressed.
 */
function CategoryPicker(props: {
	profile: string;
	categories: SidebarCategory[];
	/** The category the profile is filed under, null for Other. */
	placedIn: string | null;
	onPlace: (profile: string, categoryId: string | null) => Promise<boolean>;
}) {
	const [open, setOpen] = useState(false);
	const choices: Array<{ id: string | null; name: string }> = [...props.categories, { id: null, name: OTHER_NAME }];
	const current = choices.find((c) => c.id === props.placedIn) ?? choices[choices.length - 1]!;
	return (
		<>
			<button
				className="ghost category-picker"
				aria-expanded={open}
				aria-label="移到分类"
				title={`分类：${current.name}`}
				onClick={() => setOpen(!open)}
			>
				<TagIcon size={15} />
				<span className="current">{current.name}</span>
			</button>
			{open && (
				<div className="category-choices">
					{choices.map((c) => (
						<button
							key={c.id ?? ""}
							className="ghost"
							aria-pressed={c.id === current.id}
							onClick={async () => {
								if (c.id === current.id || (await props.onPlace(props.profile, c.id))) setOpen(false);
							}}
						>
							{c.name}
						</button>
					))}
				</div>
			)}
		</>
	);
}

/**
 * Second screen: one profile's projects and conversations, with the ways to start another at the
 * top. A profile that cannot load takes no new conversation, so it gets the list alone. Its head
 * files it under a category once the person has one, stars it and opens its configuration.
 */
function ProfileSessions(props: {
	group: SessionGroup;
	starred: boolean;
	onToggleStar: (profile: string, starred: boolean) => void;
	categories: SidebarCategory[];
	placedIn: string | null;
	onPlace: (profile: string, categoryId: string | null) => Promise<boolean>;
	actions: ListActions;
	onBack: () => void;
	onNewProject: (profile: string) => void;
	onOpenConfig: (profile: string) => void;
}) {
	const { group } = props;
	const ok = group.status === "ok";
	const category = props.categories.find((c) => c.id === props.placedIn)?.name ?? OTHER_NAME;
	const count = conversationCount(group);
	const narrow = useIsNarrow();
	return (
		<section className="drill">
			<div className="drill-head">
				<button className="ghost back icon-btn" aria-label="返回" title="返回" onClick={props.onBack}>
					<ChevronLeftIcon size={24} />
				</button>
				<span className="drill-gap" />
				{props.categories.length > 0 && (
					<CategoryPicker profile={group.profile} categories={props.categories} placedIn={props.placedIn} onPlace={props.onPlace} />
				)}
				<StarToggle profile={group.profile} starred={props.starred} onToggle={props.onToggleStar} />
				{ok && group.summary && (
					<button className="icon" title={`${group.profile} 的配置`} aria-label={`${group.profile} 的配置`} onClick={() => props.onOpenConfig(group.profile)}>
						<GearIcon size={20} />
					</button>
				)}
			</div>
			<div className="drill-scroll">
				<div className="profile-hero">
					<Avatar name={group.profile} size={narrow ? 72 : 48} />
					<div className="profile-hero-text">
						<span className="name">{group.profile}</span>
						<span className="sub">{[count > 0 ? `${count} 个对话` : "暂无对话", category].join(" · ")}</span>
					</div>
				</div>
				{ok ? (
					<div className="drill-actions">
						<button className="new-session" onClick={() => props.actions.onNewSession(group.profile)}>
							<PlusIcon size={22} />
							<span>新建对话</span>
						</button>
						<button className="new-session" onClick={() => props.onNewProject(group.profile)}>
							<FolderPlusIcon size={22} />
							<span>新建项目</span>
						</button>
						<button className="new-session anonymous" onClick={() => props.actions.onNewAnonymousSession(group.profile)}>
							<EyeOffIcon size={22} />
							<span>匿名对话</span>
						</button>
					</div>
				) : (
					<p className="hint drill-problem">
						{group.status === "broken" ? `加载失败，请运行 agent check ${group.profile}` : "profile 已不存在"}
					</p>
				)}
				<GroupBody group={group} actions={props.actions} />
			</div>
		</section>
	);
}

/**
 * A draft the builder wrote, shown under the profiles it is not yet one of (ADR-0014). The one
 * action is active, which first asks who may see it; the answer is the profile's owner. A draft with a
 * check error, or one sharing its name with a hand-written profile, shows why and takes no click.
 * An enabled draft is a profile now and only comes back here once edited; re-enabling keeps its owner.
 */
function DraftRow(props: {
	draft: DraftSummary;
	busy: boolean;
	onEnable: (name: string, owner: "me" | "shared" | "keep") => void;
}) {
	const { draft } = props;
	const [asking, setAsking] = useState(false);
	const errors = draft.diagnostics.filter((d) => d.severity === "error");
	const blocked = errors.length > 0 || draft.collides;
	const tag = draft.collides
		? { text: "与手写 profile 同名", bad: true }
		: errors.length
			? { text: "校验失败", bad: true }
			: draft.enabled
				? { text: "有更新", bad: false }
				: null;
	return (
		<div className="draft-row">
			<div className="head">
				<Avatar name={draft.name} size={40} />
				<span className="name">{draft.name}</span>
				{tag && (
					<span className={`tag${tag.bad ? " bad" : ""}`}>
						{tag.text}
					</span>
				)}
			</div>
			{errors.slice(0, 3).map((d, i) => (
				<span key={i} className="diag">
					{d.where}: {d.message}
				</span>
			))}
			<div className="actions">
				{asking ? (
					<>
						可见范围：
						<button
							onClick={() => {
								setAsking(false);
								props.onEnable(draft.name, "me");
							}}
						>
							仅自己
						</button>
						<button
							onClick={() => {
								setAsking(false);
								props.onEnable(draft.name, "shared");
							}}
						>
							两人共享
						</button>
						<button
							className="ghost"
							onClick={() => setAsking(false)}
						>
							取消
						</button>
					</>
				) : (
					<button
						disabled={blocked || props.busy}
						onClick={() =>
							draft.enabled
								? props.onEnable(draft.name, "keep")
								: setAsking(true)
						}
					>
						{props.busy
							? "启用中…"
							: draft.enabled
								? "重新启用"
								: "启用"}
					</button>
				)}
			</div>
		</div>
	);
}

export function Sidebar(props: {
	user: User;
	/** Counts beside the page buttons. */
	badges: NavBadges;
	/** Every account, for naming who a session is shared with or came from. */
	users: User[];
	profiles: ProfileSummary[];
	broken: Array<{ name: string }>;
	/** The builder's drafts; empty when no profile is the builder. */
	drafts: DraftSummary[];
	enablingDraft: string | null;
	onEnableDraft: (name: string, owner: "me" | "shared" | "keep") => void;
	/** The profile just enabled, for the one-time reminder to pull it back into the repo. */
	justEnabled: string | null;
	sessions: SessionSummary[];
	projects: ProjectSummary[];
	/** The person's stars, categories, open folds and recent uses. Without it every profile sits in a folded Other. */
	view?: SidebarView | null;
	/** Stars a profile, or unstars it with false. */
	onToggleStar: (profile: string, starred: boolean) => void;
	/** Opens or closes a fold: a category, Other, or a project in a profile's list. */
	onToggleFold: (key: ExpandedKey, open: boolean) => void;
	/** Each resolves true once the server has taken the change. */
	onCreateCategory: (name: string) => Promise<boolean>;
	onRenameCategory: (id: string, name: string) => Promise<boolean>;
	onRemoveCategory: (id: string) => Promise<boolean>;
	/** Files a profile under a category, or under Other with null. */
	onPlaceProfile: (profile: string, categoryId: string | null) => Promise<boolean>;
	currentSessionId: string | null;
	/** The project whose page is open, for marking it in the list. */
	currentProjectId: string | null;
	theme: "dark" | "light";
	onThemeToggle: () => void;
	onNewSession: (profile: string, projectId?: string) => void;
	/** An anonymous conversation on this profile: it reads no memory and is never scanned. */
	onNewAnonymousSession: (profile: string) => void;
	onNewProject: (profile: string) => void;
	onOpenProject: (id: string) => void;
	onOpenConfig: (profile: string) => void;
	page: Page;
	onOpenPage: (page: NavPage) => void;
	onOpenSession: (id: string) => void;
	onArchiveSession: (id: string) => void;
	/** Files a conversation with no project under one of its profile's projects. */
	onFileSession?: (sessionId: string, projectId: string) => void;
	onLogout: () => void;
	onClose: () => void;
}) {
	const groups = groupSessions({
		profiles: props.profiles,
		broken: props.broken,
		sessions: props.sessions,
		projects: props.projects,
		currentSessionId: props.currentSessionId,
	});
	const arranged = arrangeSidebar(groups, props.view ?? null);
	const stars = new Set(props.view?.stars ?? []);
	const narrow = useIsNarrow();
	// An enabled draft is a profile now and sits in the list above; it comes back here only when edited.
	const pending = props.drafts.filter((d) => !d.enabled || d.changed);
	/* Which profile the second screen is showing, and null for the profile list. Kept here because
	   it is where you are in the sidebar, not application state -- and kept across an open/close on
	   purpose: switching conversations inside one profile is the common move, and resetting to the
	   list would charge a tap for it every time. */
	const [drill, setDrill] = useState<string | null>(null);
	const [dragging, setDragging] = useState<SessionSummary | null>(null);
	// A reload can drop the profile the second screen was opened for.
	const drilled = drill
		? (groups.find((g) => g.profile === drill) ?? null)
		: null;

	const profileRow = (group: SessionGroup) => (
		<ProfileRow key={group.profile} group={group} starred={stars.has(group.profile)} onToggleStar={props.onToggleStar} onOpen={setDrill} />
	);

	const actions: ListActions = {
		me: props.user.id,
		nameOf: (userId) =>
			props.users.find((u) => u.id === userId)?.name ?? userId,
		currentSessionId: props.currentSessionId,
		currentProjectId:
			props.page === "project" ? props.currentProjectId : null,
		onOpenSession: props.onOpenSession,
		onArchiveSession: props.onArchiveSession,
		onOpenProject: props.onOpenProject,
		onNewSession: props.onNewSession,
		onNewAnonymousSession: props.onNewAnonymousSession,
		expanded: props.view ? new Set(props.view.expanded) : null,
		onToggleFold: props.onToggleFold,
		dragging,
		onDragSession: props.onFileSession ? setDragging : undefined,
		onFileSession: props.onFileSession,
	};

	const themeLabel = props.theme === "dark" ? "切换到日间配色" : "切换到夜间配色";
	const close = (
		<button className="ghost sidebar-toggle icon-btn" title="收起边栏" aria-label="收起边栏" onClick={props.onClose}>
			<ChevronLeftIcon size={22} />
		</button>
	);

	return (
		<aside className="sidebar">
			{drilled ? null : narrow ? (
				<header className="drawer-head">
					<Avatar name={props.user.name} size={38} />
					<span className="who">{props.user.name}</span>
					<button className="ghost icon-btn theme-toggle" onClick={props.onThemeToggle} aria-label={themeLabel} title={themeLabel}>
						{props.theme === "dark" ? <SunIcon size={21} /> : <MoonIcon size={21} />}
					</button>
					<button className="ghost icon-btn" aria-label="退出" title="退出" onClick={props.onLogout}>
						<LogOutIcon size={21} />
					</button>
					{close}
				</header>
			) : (
				<header className="panel-head">
					<h1>对话</h1>
					{close}
				</header>
			)}

			<div className="sidebar-scroll">
				{narrow && !drilled && (
					<nav className="nav-pages" aria-label="页面">
						{NAV_PAGES.map(({ page, label, Icon, tint }) => (
							<button key={page} className={`nav-card${props.page === page ? " current" : ""}`} onClick={() => props.onOpenPage(page)}>
								<span className={`nav-icon tint-${tint}`}>
									<Icon size={17} />
								</span>
								{label}
								<Badge page={page} count={props.badges[page]} />
							</button>
						))}
					</nav>
				)}
				{drilled ? (
					<ProfileSessions
						group={drilled}
						starred={stars.has(drilled.profile)}
						onToggleStar={props.onToggleStar}
						categories={props.view?.categories ?? []}
						placedIn={props.view?.placements[drilled.profile] ?? null}
						onPlace={props.onPlaceProfile}
						actions={actions}
						onBack={() => setDrill(null)}
						onNewProject={props.onNewProject}
						onOpenConfig={props.onOpenConfig}
					/>
				) : (
					<section className="groups">
						{groups.length === 0 && (
							<p className="hint">
								暂无可用 profile，请运行 <code>npm run agent check</code>
							</p>
						)}
						{arranged.top.length > 0 && (
							<>
								<h2 className="list-label">常用</h2>
								<div className="top-profiles list-card" role="group" aria-label="常用">
									{arranged.top.map(profileRow)}
								</div>
							</>
						)}
						{groups.length > 0 && (
							<>
								<h2 className="list-label">分类</h2>
								<div className="list-card categories">
									{arranged.categories.map((category) => (
										<CategoryFold
											key={category.key}
											category={category}
											remembered={Boolean(props.view)}
											onToggle={props.onToggleFold}
											onRename={props.onRenameCategory}
											onRemove={props.onRemoveCategory}
										>
											{category.groups.map(profileRow)}
										</CategoryFold>
									))}
									<NewCategory onCreate={props.onCreateCategory} />
								</div>
							</>
						)}
						{pending.length > 0 && (
							<>
								<h2 className="list-label">草稿 · 未启用</h2>
								<div className="drafts-group list-card">
									{pending.map((d) => (
										<DraftRow key={d.name} draft={d} busy={props.enablingDraft === d.name} onEnable={props.onEnableDraft} />
									))}
								</div>
							</>
						)}
					</section>
				)}
			</div>
		</aside>
	);
}
